import { Add, Delete, Edit } from '@mui/icons-material';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  IconButton,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import { Box } from '@mui/system';
import { VisitTemplate } from 'api/models/VisitTemplate';
import useHasAccess from 'hooks/useHasAccess';
import useQuery from 'hooks/useQuery';
import { query, remove } from 'operations';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  ToastSeverity,
  confirmDialogState,
  dialogState,
  formTemplateTypeListState,
  selectedVisitTemplateState,
  toastStateSelector,
  visitTemplateListState,
} from 'state';
import { Permission } from 'state/permissionState';
import { DialogTypes, GeneralIds, ModelTypes } from 'types/types';

export default function VisitTemplateTable() {
  const { t, i18n } = useTranslation();
  const { projectId } = useParams<GeneralIds>();
  const setConfirmDialog = useSetRecoilState(confirmDialogState);
  const openDialog = useSetRecoilState(dialogState);
  const [visitTemplates, setVisitTemplates] = useRecoilState(
    visitTemplateListState,
  );
  const setToast = useSetRecoilState(toastStateSelector);
  const setSelectedVisitTemplate = useSetRecoilState(
    selectedVisitTemplateState,
  );
  const formTemplateTypes = useRecoilValue(formTemplateTypeListState);

  const canEditAndDeleteVisitTemplateTypes = useHasAccess([
    Permission.WRITE_VISIT_TEMPLATE,
  ]);

  const { loading } = useQuery<VisitTemplate[]>({
    type: ModelTypes.visitTemplate,
    ids: { projectId: projectId },
    set: {
      data: setVisitTemplates,
    },
  });

  const onConfirmDelete = (templateId: string) => {
    return async () => {
      try {
        await remove({
          type: ModelTypes.visitTemplate,
          ids: {
            id: templateId,
            projectId: projectId,
          },
        });

        const { data: visitTemplates } = await query<VisitTemplate[]>({
          type: ModelTypes.visitTemplate,
          ids: {
            projectId: projectId,
          },
        });
        setVisitTemplates(visitTemplates);
        setConfirmDialog({ isOpen: false });

        setToast({
          isOpen: true,
          message: t('general.success.delete'),
          severity: ToastSeverity.success,
        });
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18n.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('projectCreation.errors.deletionFailed'),
          severity: ToastSeverity.error,
        });
      }
    };
  };

  return (
    <>
      <Box display="flex" flexDirection="row" justifyContent={'space-between'}>
        <Typography variant="h5" ml={1} mt={2}>
          {t('visits.visitTemplates')}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<Add />}
          sx={{ mr: 3, mt: 2 }}
          disabled={!canEditAndDeleteVisitTemplateTypes}
          onClick={() =>
            openDialog({
              isOpen: true,
              dialogType: DialogTypes.addVisitTemplate,
            })
          }
        >
          {t('visits.addVisitTemplate')}
        </Button>
      </Box>
      <TableContainer sx={{ mt: 1, minHeight: '90px' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }} align="left">
                {t('general.name')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="left">
                {t('visits.supportedFormTemplateTypes')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">
                {t('visits.repeatConfig')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">
                {t('visits.orderConfig')}
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                {t('general.operations')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ position: 'relative', minHeight: '40px' }}>
            {loading ? (
              <LinearProgress
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                }}
              />
            ) : visitTemplates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography align="center">{t('general.noData')}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              visitTemplates.map(visitTemplate => (
                <TableRow key={visitTemplate.id}>
                  <TableCell align="left">{visitTemplate.name}</TableCell>
                  <TableCell align="left">
                    <Box
                      display="flex"
                      flexDirection={'row'}
                      alignItems={'center'}
                      justifyContent={'start'}
                      gap={1}
                      flexWrap={'wrap'}
                    >
                      {visitTemplate.supportedFormTemplateTypes.map(
                        templateId => (
                          <Chip
                            key={templateId}
                            label={
                              formTemplateTypes?.find(
                                el => el.id === templateId,
                              )?.name
                            }
                            color={'primary'}
                          />
                        ),
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {visitTemplate.repeatConfig}
                  </TableCell>
                  <TableCell align="center">
                    {t(`visits.orderConfigValues.${visitTemplate.orderConfig}`)}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      disabled={!canEditAndDeleteVisitTemplateTypes}
                      onClick={() => {
                        setSelectedVisitTemplate(visitTemplate);
                        openDialog({
                          isOpen: true,
                          dialogType: DialogTypes.addVisitTemplate,
                        });
                      }}
                    >
                      <Tooltip title={t('general.edit')}>
                        <Edit fontSize="small" />
                      </Tooltip>
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={!canEditAndDeleteVisitTemplateTypes}
                      onClick={() => {
                        setConfirmDialog({
                          isOpen: true,
                          dialogType: DialogTypes.confirmDelete,
                          action: onConfirmDelete(visitTemplate.id),
                        });
                      }}
                    >
                      <Tooltip title={t('general.delete')}>
                        <Delete fontSize="small" />
                      </Tooltip>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
